import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Button, Typography, withStyles } from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { MapPin } from 'phosphor-react';
import moment from 'moment';

import { capitalize, formatSchoolLevel } from '../../../lib/helpers';
import ToriiLoader from '../../utility/ToriiLoader';
import NoResults from '../../utility/NoResults';
import { isMobile } from '../../../lib/getDeviceSize';
import { DONUT_COLORS } from '../../../lib/constants';
import styles from './styles';

const DONUT_SIZE = isMobile() ? 60 : 80;
const DONUT_STROKE_WIDTH = isMobile() ? 6 : 8;

interface Props {
  classes: any;
  schools: any;
  loading: boolean;
  openInModal?: boolean;
}
interface State {
  selectedFilter: number;
  showMinimum: boolean;
}

const filterTypes: any = {
  all: 0,
  elementary: 1,
  middle: 2,
  high: 3,
};

export class Schools extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      selectedFilter: 0,
      showMinimum: true,
    };
  }

  handleTabChange = (selectedFilter: number) => {
    this.setState({ selectedFilter });
  };

  _renderSchoolsFilter = (classes: any) => {
    return (
      <div className={classes.schoolFilterContainer}>
        {Object.keys(filterTypes).map((filter: string, index: number) => {
          const isSelected = this.state.selectedFilter === filterTypes[filter];
          return (
            <Button
              key={`${index}`}
              className={
                isSelected ? classes.schoolTabSelected : classes.schoolTab
              }
              onClick={() => this.handleTabChange(filterTypes[filter])}
            >
              <Typography
                className={classes.tabButtonText}
                style={{ color: isSelected ? 'white' : '#323232' }}
              >
                {filter}
              </Typography>
            </Button>
          );
        })}
      </div>
    );
  };

  _getRatingColor = (rating: string | undefined) => {
    let backgroundColor = DONUT_COLORS.noRating;
    if (rating) {
      backgroundColor =
        rating === 'Below average'
          ? DONUT_COLORS.poor
          : rating === 'Average'
          ? DONUT_COLORS.average
          : rating === 'Above average'
          ? DONUT_COLORS.good
          : DONUT_COLORS.noRating;
    }
    return backgroundColor;
  };

  _renderRating = (ratingData: any) => {
    const ratingScore = ratingData.rating;
    return (
      <div className={this.props.classes.schoolRatingContainer}>
        <a href={ratingData.overviewUrl} target="_blank">
          <img
            src={`../../../static/img/greatschools/${
              ratingScore ? ratingScore.toLowerCase().replace(' ', '_') : 'na'
            }.png`}
            style={{
              height: 60,
              width: 60,
            }}
          />
        </a>
        <Typography
          className={this.props.classes.donutLabel}
          style={{ marginTop: 5 }}
        >
          Rating
        </Typography>
      </div>
    );
  };

  _renderSchoolDetails = (classes: any, data: any, empty?: boolean) => {
    const ratingColor = this._getRatingColor(data.rating);
    if (empty || Object.keys(data).length === 0) {
      return (
        <div
          key={`blank`}
          className={classes.schoolDetailContainer}
          style={{ backgroundColor: '#E4E4E4', height: 76 }}
        >
          <Typography
            style={{ alignSelf: 'center', color: '#999999', marginLeft: 10 }}
          >
            No schools currently available
          </Typography>
        </div>
      );
    }
    return (
      <div
        key={`${data.universalId}`}
        className={classes.schoolDetailContainer}
      >
        <div className={classes.schoolContent}>
          <div className={classes.schoolContentInner}>
            <a
              className={classes.schoolName}
              href={data.overviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: ratingColor.indicatorColor }}
            >
              {data.name}
            </a>
          </div>
          <div className={classes.schoolContentInner}>
            <Typography className={classes.schoolContentText}>
              {formatSchoolLevel(data.level)} / {capitalize(data.type)}
            </Typography>
          </div>
          <div className={classes.schoolContentInner}>
            <MapPin
              size={16}
              color="#393939"
              weight="fill"
              style={{ marginRight: 5, marginLeft: -3 }}
            />
            <Typography
              className={classes.schoolContentText}
              style={{ padding: '0 15px 0 0' }}
            >
              {_.round(data.distance, 2)} Miles
            </Typography>
          </div>
        </div>
        {this._renderRating(data)}
      </div>
    );
  };

  _handleToggle = () => {
    this.setState({
      showMinimum: !this.state.showMinimum,
    });
  };

  _renderDropdownToggle = (
    classes: any,
    showMinimum: boolean,
    disabled?: boolean,
  ) => {
    if (disabled) {
      return;
    }
    return (
      <div
        className={classes.schoolDetailContainer}
        style={{ backgroundColor: '#FFFFFF', height: 76 }}
      >
        <div className={classes.schoolToggleTextBox}>
          <Typography
            className={classes.schoolToggleText}
            style={{
              color: disabled ? '#E4E4E4' : '#323232',
            }}
            onClick={this._handleToggle}
          >
            View {showMinimum ? 'More' : 'Less'}
          </Typography>
        </div>
        <div className={classes.schoolToggleButtonBox}>
          <Button
            disabled={disabled}
            onClick={this._handleToggle}
            className={classes.expandButton}
            style={{ marginTop: 0 }}
          >
            {showMinimum ? (
              <ExpandMore style={{ color: disabled ? '#E4E4E4' : '#323232' }} />
            ) : (
              <ExpandLess style={{ color: disabled ? '#E4E4E4' : '#323232' }} />
            )}
          </Button>
        </div>
      </div>
    );
  };

  _filterData = (
    schoolDatas: any,
    selectedFilter: number,
    showMinimum: boolean,
  ) => {
    if (selectedFilter === 0) {
      // All
      return schoolDatas;
    }
    if (selectedFilter === 1) {
      // Elementary
      const filteredData = schoolDatas.filter((data: any) =>
        data.levelCodes.includes('e'),
      );
      return filteredData;
    } else if (selectedFilter === 2) {
      // Middle
      const filteredData = schoolDatas.filter((data: any) =>
        data.levelCodes.includes('m'),
      );
      return filteredData;
    } else if (selectedFilter === 3) {
      // High
      const filteredData = schoolDatas.filter((data: any) =>
        data.levelCodes.includes('h'),
      );
      return filteredData;
    } else {
      return schoolDatas;
    }
  };

  _renderSchoolData = (filteredData: any) => {
    let renderData = [];
    renderData = filteredData.map((data: any, index: number) => {
      return this._renderSchoolDetails(this.props.classes, data);
    });
    // Appending a blank box at the end
    if (filteredData.length === 0) {
      renderData.push(this._renderSchoolDetails(this.props.classes, {}, true));
    }
    return renderData;
  };

  render() {
    const { classes, schools, loading, openInModal } = this.props;
    const { selectedFilter, showMinimum } = this.state;
    const filteredData = this._filterData(schools, selectedFilter, showMinimum);
    return (
      <div
        className={classes.detailsContainer}
        style={{ width: openInModal ? '90%' : '' }}
      >
        <div className={`row ${classes.marginContainer}`}>
          <Typography className={classes.detailsTitle}>Schools</Typography>
        </div>
        {loading ? (
          <ToriiLoader padTop={50} />
        ) : schools.length === 0 ? (
          <div>
            <NoResults
              emptyText="Oops! It looks like we couldn't find any schools nearby"
              bottomText
              padTop
            />
          </div>
        ) : (
          <React.Fragment>
            {this._renderSchoolsFilter(classes)}
            {this._renderSchoolData(
              showMinimum ? filteredData.slice(0, 3) : filteredData,
            )}
            {this._renderDropdownToggle(
              classes,
              showMinimum,
              filteredData.length <= 3 ? true : false,
            )}
          </React.Fragment>
        )}
        {!loading && schools.length !== 0 && (
          <div
            className={`row ${classes.marginContainer}`}
            style={{ alignItems: 'center' }}
          >
            <p style={{ fontSize: 12, color: '#0E1217' }}>
              Data provided by{' '}
              <a
                href="https://www.greatschools.org"
                rel="noopener noreferrer nofollow"
                style={{ color: '#0E1217' }}
                target="_blank"
              >
                GreatSchools.org
              </a>{' '}
              © {moment().format('YYYY')}. All rights reserved.
            </p>
            <a
              href="https://www.greatschools.org"
              rel="noopener noreferrer nofollow"
              target="_blank"
            >
              <img
                style={{ margin: '0 15px', height: 30 }}
                data-alt="mobile-wave"
                src="../../../static/img/greatschools/greatschools@1x.png"
              />
            </a>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  schools: state.listings.schools,
  loading: state.listings.schoolsLoading,
});

export default connect(mapStateToProps)(withStyles(styles)(Schools));
